import { enumMembers, type ApiSchema } from "./apiSchema";
import { decodeConnectorStatus, type ConnectorStatus } from "./connectorStatus";
import { jsonBody, requestJson, requestWithoutContent, withQuery } from "./http/httpClient";
import { readArray, readBoolean, readNullableString, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";

/**
 * Connectors (module A, ADR-0009; `docs/components/accounts.md`, connector-protocol.md
 * "Pairing"): approving or denying a device authorization a connector started, and the
 * user's paired connectors. Starting and polling a pairing are the connector's, not the
 * browser's. Routes: `server/src/dmw/api/routes/connectors/`.
 */

export type DeviceAuthorizationState = ApiSchema<"DeviceAuthorizationState">;
export type ConnectorState = ApiSchema<"ConnectorDeviceState">;

/** What a connector says about the PC it runs on. Shown before approval (RFC 8628 §5.4). */
export interface ConnectorDeviceDescription {
  readonly label: string;
  /** As Node names it: `win32`, `darwin`, `linux`, … */
  readonly platformOs: string;
  /** As Node names it: `x64`, `arm64`, … */
  readonly platformArch: string;
  readonly connectorVersion: string;
  /** First 16 bytes of the SHA-256 of the device's public key, hexadecimal in groups. */
  readonly keyFingerprint: string;
}

export interface DeviceAuthorization {
  /** `XXXX-XXXX`, as the server displays it. */
  readonly userCode: string;
  readonly state: DeviceAuthorizationState;
  readonly device: ConnectorDeviceDescription;
  /** ISO 8601: when the connector started the pairing. */
  readonly createdAt: string;
  /** ISO 8601: after this the code can no longer be approved. */
  readonly expiresAt: string;
}

export interface Connector {
  readonly connectorId: string;
  readonly userId: string;
  readonly state: ConnectorState;
  /** The PC's name every view shows: the one its owner gave, else the computer's own (F78). */
  readonly name: string;
  /** The name its owner gave it; `null` until renamed. */
  readonly givenName: string | null;
  /** What the connector says about itself; `device.label` is the computer's own name. */
  readonly device: ConnectorDeviceDescription;
  /** ISO 8601. */
  readonly pairedAt: string;
  /** ISO 8601, or `null` while the connector is active. */
  readonly revokedAt: string | null;
}

/** A connector as the list shows it: with its presence and latest capability report. */
export interface ListedConnector extends Connector {
  readonly status: ConnectorStatus;
  /** A person's "DMW server" host (ADR-0023, PR-1), not a PC: it runs only on the server's keys, for the people a project names. */
  readonly hostedByServer: boolean;
}

const DEVICE_AUTHORIZATION_STATES = enumMembers<DeviceAuthorizationState>({
  pending: true,
  approved: true,
  denied: true,
  expired: true,
});

const CONNECTOR_STATES = enumMembers<ConnectorState>({ active: true, revoked: true });

/** The device behind a user code, to show before the user decides. */
export function lookUpDeviceAuthorization(userCode: string, signal?: AbortSignal): Promise<DeviceAuthorization> {
  return requestJson({
    method: "GET",
    path: withQuery("/connectors/device-authorizations/lookup", { user_code: userCode }),
    signal,
    decodeResponse: (body) => decodeDeviceAuthorization(readObject(body, "device authorization")),
  });
}

/** Pair the connector with the signed-in account. */
export function approveDeviceAuthorization(userCode: string): Promise<Connector> {
  return requestJson({
    method: "POST",
    path: `/connectors/device-authorizations/${encodeURIComponent(userCode)}/approve`,
    decodeResponse: (body) => decodeConnector(readObject(body, "connector")),
  });
}

export function denyDeviceAuthorization(userCode: string): Promise<DeviceAuthorization> {
  return requestJson({
    method: "POST",
    path: `/connectors/device-authorizations/${encodeURIComponent(userCode)}/deny`,
    decodeResponse: (body) => decodeDeviceAuthorization(readObject(body, "device authorization")),
  });
}

/** The signed-in user's connectors, active and revoked, newest pairing first. */
export function listConnectors(signal?: AbortSignal): Promise<readonly ListedConnector[]> {
  return requestJson({
    method: "GET",
    path: "/connectors",
    signal,
    decodeResponse: (body) =>
      readArray(body, "connectors", (item) => {
        const connector = readObject(item, "connector");
        return {
          ...decodeConnector(connector),
          status: decodeConnectorStatus(readObject(connector["status"], "status")),
          hostedByServer: readBoolean(connector, "hosted_by_server"),
        };
      }),
  });
}

/**
 * The address connectors pair with (`threads.connector_hub.public_origin`, Q-65), for
 * `dmw-connector pair <address>`; `null` while the server has none configured.
 */
export function readConnectorServerAddress(signal?: AbortSignal): Promise<string | null> {
  return requestJson({
    method: "GET",
    path: "/connectors/server-address",
    signal,
    decodeResponse: (body) => readNullableString(readObject(body, "server address"), "origin"),
  });
}

/** Name one of your PCs (F78); `null` goes back to the computer's own name. */
export function renameConnector(connectorId: string, name: string | null): Promise<Connector> {
  return requestJson({
    method: "PATCH",
    path: `/connectors/${encodeURIComponent(connectorId)}`,
    body: jsonBody({ name }),
    decodeResponse: (body) => decodeConnector(readObject(body, "connector")),
  });
}

/** Revoke a connector: it can no longer connect. Its record stays, marked revoked. */
export function revokeConnector(connectorId: string): Promise<Connector> {
  return requestJson({
    method: "DELETE",
    path: `/connectors/${encodeURIComponent(connectorId)}`,
    decodeResponse: (body) => decodeConnector(readObject(body, "connector")),
  });
}

/**
 * Ask a connected connector of yours to read its agents' models and model catalogues and
 * its API listings again; its next capability report carries what it found (D17).
 * `agentId` `null` asks for every agent. Fails with `connector_offline` when not connected.
 */
export function refreshConnectorCapabilities(connectorId: string, agentId: string | null): Promise<void> {
  return requestWithoutContent({
    method: "POST",
    path: `/connectors/${encodeURIComponent(connectorId)}/capabilities/refresh`,
    body: { kind: "json", value: { agent_id: agentId } },
  });
}

/** A part of a connector's own page (F85). */
export type ConnectorPageSection = ApiSchema<"PageSection">;
/** What came of asking a connector to open its page: its own answer, or why it gave none. */
export type ConnectorPageOutcome = ApiSchema<"PageOpening"> | ApiSchema<"PcRequestUnanswered">;
/** What came of asking a connector to start an agent's sign-in: its own answer, or why it gave none. */
export type AgentSignInOutcome = ApiSchema<"AgentSignInStart"> | ApiSchema<"PcRequestUnanswered">;

const SIGN_IN_OUTCOMES = enumMembers<AgentSignInOutcome>({
  started: true,
  already_open: true,
  not_available: true,
  no_answer: true,
  update_needed: true,
});

/**
 * Ask a connected connector of yours to start one agent's own sign-in on its PC (F122): the
 * agent's login opens in that PC's browser, and only the person there completes it. Fails
 * with `connector_offline` when it is not connected.
 */
export function startAgentSignIn(connectorId: string, agentId: string): Promise<AgentSignInOutcome> {
  return requestJson({
    method: "POST",
    path: `/connectors/${encodeURIComponent(connectorId)}/agents/${encodeURIComponent(agentId)}/sign-in`,
    decodeResponse: (body) => readOneOf(readObject(body, "agent sign-in"), "outcome", SIGN_IN_OUTCOMES),
  });
}

/** A "Connect this PC" link's ticket (F122): the secret goes into the link only. */
export interface PairingTicket {
  readonly ticket: string;
  readonly ticketId: string;
}

/** A new ticket for this browser's "Connect this PC" link; it ends the account's older ones. */
export function issuePairingTicket(): Promise<PairingTicket> {
  return requestJson({
    method: "POST",
    path: "/connectors/pairing-tickets",
    decodeResponse: (body) => {
      const issued = readObject(body, "pairing ticket");
      return { ticket: readString(issued, "ticket"), ticketId: readString(issued, "ticket_id") };
    },
  });
}

export type PairingTicketState = ApiSchema<"PairingTicketState">;

const PAIRING_TICKET_STATES = enumMembers<PairingTicketState>({ pending: true, used: true, superseded: true, expired: true });

/** Your ticket's state, and the connector it paired once one did. */
export function readPairingTicket(ticketId: string, signal?: AbortSignal): Promise<{ readonly state: PairingTicketState; readonly connectorId: string | null }> {
  return requestJson({
    method: "GET",
    path: `/connectors/pairing-tickets/${encodeURIComponent(ticketId)}`,
    signal,
    decodeResponse: (body) => {
      const ticket = readObject(body, "pairing ticket");
      return { state: readOneOf(ticket, "state", PAIRING_TICKET_STATES), connectorId: readNullableString(ticket, "connector_id") };
    },
  });
}

const PAGE_OUTCOMES = enumMembers<ConnectorPageOutcome>({
  opened: true,
  too_soon: true,
  browser_never: true,
  no_browser: true,
  no_page: true,
  no_answer: true,
  update_needed: true,
});

/**
 * Ask a connected connector of yours to open its own page in its PC's browser at `section`,
 * signed in with a link only it makes (F85); resolves with its answer. Fails with
 * `connector_offline` when it is not connected.
 */
export function openConnectorPage(connectorId: string, section: ConnectorPageSection): Promise<ConnectorPageOutcome> {
  return requestJson({
    method: "POST",
    path: `/connectors/${encodeURIComponent(connectorId)}/page/open`,
    body: { kind: "json", value: { section } },
    decodeResponse: (body) => readOneOf(readObject(body, "page opening"), "outcome", PAGE_OUTCOMES),
  });
}

function decodeDeviceAuthorization(authorization: JsonObject): DeviceAuthorization {
  return {
    userCode: readString(authorization, "user_code"),
    state: readOneOf(authorization, "state", DEVICE_AUTHORIZATION_STATES),
    device: decodeDevice(readObject(authorization["device"], "device")),
    createdAt: readString(authorization, "created_at"),
    expiresAt: readString(authorization, "expires_at"),
  };
}

function decodeConnector(connector: JsonObject): Connector {
  return {
    connectorId: readString(connector, "connector_id"),
    userId: readString(connector, "user_id"),
    state: readOneOf(connector, "state", CONNECTOR_STATES),
    name: readString(connector, "name"),
    givenName: readNullableString(connector, "given_name"),
    device: decodeDevice(readObject(connector["device"], "device")),
    pairedAt: readString(connector, "paired_at"),
    revokedAt: readNullableString(connector, "revoked_at"),
  };
}

function decodeDevice(device: JsonObject): ConnectorDeviceDescription {
  const platform = readObject(device["platform"], "platform");
  return {
    label: readString(device, "label"),
    platformOs: readString(platform, "os"),
    platformArch: readString(platform, "arch"),
    connectorVersion: readString(device, "connector_version"),
    keyFingerprint: readString(device, "key_fingerprint"),
  };
}
